<template>
  <div v-if="adresse">
    <b-field :label="$t('address.addressCommercial')">
      <gmap-autocomplete
        class="gmap-auto"
        selectFirstOnEnter
        @place_changed="miseAJourCoordonneeAdresse($event)"
        @change.native="stockValeurSelected($event);"
        :placeholder="$t('address.searchAddressByGoogleMap')"
        :value="autocomplete"
        :componentRestrictions="{ country: listeAlpha2CodePaysLimiteAutocompleteGoogle }">
      </gmap-autocomplete>
    </b-field>
    <b-field>
      <b-input :placeholder="$t('address.address') + ' 1'"
               v-model="adresse.adresse1"></b-input>
    </b-field>
    <b-field>
      <b-input :placeholder="$t('address.address') + ' 2'"
               v-model="adresse.adresse2"></b-input>
    </b-field>
    <b-field>
      <b-input :placeholder="$t('address.address') + ' 3'"
               v-model="adresse.adresse3"></b-input>
    </b-field>
    <div class="columns is-multiline">
      <div class="column is-4">
        <b-field :label="$t('address.zipCode')">
          <b-input :placeholder="$t('address.zipCode')"
                   v-model="adresse.codePostal"></b-input>
        </b-field>
      </div>
      <div class="column is-4">
        <b-field :label="$t('address.city')" class="obligatoire">
          <b-autocomplete :placeholder="$t('address.city')"
              v-model="adresse.ville"
              :data="villesAutocomplete"
              :loading="isFetching"
              :open-on-focus="true"
              @typing="getVilles"
              @select="option => this.adresse.ville = option">
            </b-autocomplete>
        </b-field>
      </div>
      <div class="column is-4">
        <b-field :label="$t('address.country')">
          <b-autocomplete :placeholder="$t('address.country')"
                v-model="adresse.pays"
                :data="paysAutocomplete"
                :loading="isFetchingPays"
                :open-on-focus="true"
                @typing="getPays"
                @select="option => this.adresse.pays = option">
              </b-autocomplete>
        </b-field>
      </div>
      <div class="column is-6">
        <b-field :label="$t('address.departement')">
          <b-input :placeholder="$t('address.departement')"
                   v-model="adresse.departement"></b-input>
        </b-field>
      </div>
      <div class="column is-6">
        <b-field :label="$t('address.agglomeration')">
          <b-input :placeholder="$t('address.agglomeration')"
                   v-model="adresse.agglomeration"></b-input>
        </b-field>
      </div>
    </div>
    <div class="columns mgt-10px">
      <gmap-map class="column is-12 is-height-320" :center="center"
                :zoom="adresse.longitude !== null && adresse.latitude !== null
                ? zoomDefault : zoomDefaultSansMarker">
        <div v-if="adresse.longitude && adresse.latitude">
        <gmap-marker :key="m.num" v-for="(m) in markers"
                     :position="m.position" :draggable="true" :icon="m.icon"
                     @dragend="updateCoordinates">
        </gmap-marker>
        </div>
      </gmap-map>
    </div>
    <div class="columns">
      <b-field :label="$t('catalogue.lat')" class="column is-6">
        <input-coordonnee v-model="adresse.latitude"></input-coordonnee>
      </b-field>
      <b-field :label="$t('catalogue.long')" class="column is-6">
        <input-coordonnee v-model="adresse.longitude"></input-coordonnee>
      </b-field>
    </div>
  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
import debounce from 'lodash/debounce';
import markersGetter from '@/services/general/map/markersGetter';
import centerMapGetter from '@/services/general/map/centerMapGetter';
import inputCoordonnee from '@/components/general/inputCoordonnee';
import {
  ZOOM_DEFAULT_EMPLACEMENT,
  CENTER_DEFAULT,
  ZOOM_DEFAULT_SANS_MARKER,
  COULEUR_PUCE_DEFAULT,
} from '@/configuration/general/carteConf';
import getGmapAdressComponentMapping from '@/services/general/map/gmapAdressComponentMapping';
import getAllVilles from '@/services/ville/getAllVilles';
import getAllPays from '@/services/pays/getAllpays';
import messageErreur from '@/services/general/messageErreur';
import { PARAMETRES_INTERNE_KEYS } from '@/store/modules/parametres-mutation-type';

const Parametres = createNamespacedHelpers('parametres');

export default {
  props: {
    tableName: {
      type: String,
      required: true,
    },
    adresse: {
      type: Object,
      required: true,
    },
    azimut: {
      type: Number,
      required: false,
    },
  },
  components: {
    inputCoordonnee,
  },
  data() {
    return {
      zoomDefault: ZOOM_DEFAULT_EMPLACEMENT,
      zoomDefaultSansMarker: ZOOM_DEFAULT_SANS_MARKER,
      autocomplete: '',
      value: '',
      villesAutocomplete: [],
      paysAutocomplete: [],
      isFetching: false,
      isFetchingPays: false,
      gmapAddressComponentMapping: getGmapAdressComponentMapping(),
    };
  },
  computed: {
    ...Parametres.mapGetters({
      parametresInterne: 'parametresInterne',
    }),
    markers() {
      if (this.azimut !== null) {
        return markersGetter(this.adresse, COULEUR_PUCE_DEFAULT, this.azimut);
      }
      return markersGetter(this.adresse);
    },
    center() {
      if (this.adresse.longitude !== null
        && this.adresse.latitude !== null) {
        return centerMapGetter(this.adresse, this.markers);
      }
      return centerMapGetter(CENTER_DEFAULT, []);
    },
    listeAlpha2CodePaysLimiteAutocompleteGoogle() {
      const param = this.parametresInterne(
        PARAMETRES_INTERNE_KEYS.listeAlpha2CodePaysLimiteAutocompleteGoogle.key,
      );
      if (!param.length) return [];
      return param.split('|').map(el => el.trim());
    },
  },
  methods: {
    ...Parametres.mapActions({
      loadParametresInterne: 'loadParametresInterne',
    }),
    getVilles: debounce(async function getVilles(villeName) {
      try {
        this.isFetching = true;
        this.villesAutocomplete = await getAllVilles(villeName, this.tableName);
      } catch (e) {
        messageErreur(this.$i18n);
        throw e;
      } finally {
        this.isFetching = false;
      }
    }, 300),
    getPays: debounce(async function getPays(countryName) {
      try {
        this.isFetchingPays = true;
        this.paysAutocomplete = await getAllPays(countryName);
      } catch (e) {
        messageErreur(this.$i18n);
        throw e;
      } finally {
        this.isFetchingPays = false;
      }
    }, 300),
    updateCoordinates(location) {
      this.adresse.longitude = location.latLng.lng();
      this.adresse.latitude = location.latLng.lat();
    },
    stockValeurSelected(e) {
      this.value = e.target.value;
    },
    miseAJourCoordonneeAdresse(place) {
      Object.keys(this.adresse).forEach(key => {
        this.adresse[key] = '';
      });
      place.address_components.forEach(element => {
        element.types.forEach(type => {
          if (Object.prototype.hasOwnProperty.call(this.gmapAddressComponentMapping, type)) {
            const mapping = this.gmapAddressComponentMapping[type];
            this.adresse[mapping.field] += element.long_name;
            if (mapping.separator) {
              this.adresse[mapping.field] += mapping.separator;
            }
          }
        });
      });
      if (place.types
        && place.types.length > 0
        && place.types[place.types.length - 1] === 'establishment'
        && place.name) {
        this.adresse.intitule1 = place.name;
      }
      this.adresse.latitude = place.geometry.location.lat();
      this.adresse.longitude = place.geometry.location.lng();
      this.autocomplete = this.value;
    },
  },
  async mounted() {
    try {
      await this.loadParametresInterne(
        [PARAMETRES_INTERNE_KEYS.listeAlpha2CodePaysLimiteAutocompleteGoogle.key],
      );
    } catch (e) {
      messageErreur(this.$i18n);
      throw e;
    }
  },
};
</script>

<style scoped>

</style>
